import { SizableText, YStack } from '@mythical/ui'
import { useStores } from 'app/store'
import { useCallback } from 'react'

import { ButtonLink } from 'app/components/primitive/Button'
import Section from 'app/components/structure/Section'
import { ALL_ACCESS_TIER } from 'app/constants/tiers'
import { ContentCarousel } from 'app/components/content/ContentCarousel'
import CollectibleCard, { CollectibleProps } from './CollectibleCard'

type QuarterlyCollectiblesProps = {
  items?: CollectibleProps[] | undefined
  showUpsellPrompt?: boolean
}

const QuarterlyCollectibles = ({
  items,
  showUpsellPrompt = true,
}: QuarterlyCollectiblesProps) => {
  const { userStore } = useStores()
  const isLoggedIn = !!userStore?.user?.id
  const canMemberfulLink = userStore?.canMemberfulLink ?? false
  const isEligible = userStore?.user?.tier !== ALL_ACCESS_TIER.name

  const renderItem = useCallback(
    (item: CollectibleProps) => <CollectibleCard {...item} />,
    [],
  )

  const keyExtractor = useCallback((item: CollectibleProps) => item.id, [])

  if (!items || items.length === 0) return null

  const now = new Date()
  const toDate = (value?: string | null) => {
    if (!value) return null
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const itemMeta = items.map((item) => {
    const availableFrom = toDate(item.availableFrom)
    const availableUntil = toDate(item.availableUntil)
    const isFuture = !!(availableFrom && availableFrom > now)
    const isExpired = !!(availableUntil && availableUntil < now)

    return { isFuture, isExpired }
  })

  const currentIndex = itemMeta.findIndex(
    (meta) => !meta.isFuture && !meta.isExpired,
  )
  const resolvedCurrentIndex = currentIndex >= 0 ? currentIndex : 0

  const normalizedItems = items.map((item, index) => ({
    ...item,
    isPastByPosition:
      index !== resolvedCurrentIndex && !itemMeta[index].isFuture,
  }))

  return (
    <Section paddedX gap="$5" $gtMd={{ gap: '$6' }}>
      <YStack $gtSm={{ overflow: 'hidden' }}>
        <ContentCarousel
          items={normalizedItems}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
        />
      </YStack>
      {showUpsellPrompt && isEligible && canMemberfulLink && (
        <YStack gap="$3.5">
          <SizableText
            ff="$heading"
            tag="h2"
            size="$s"
            tt="uppercase"
            ta="center"
            $gtSm={{ size: '$m' }}
          >
            {isLoggedIn
              ? 'Upgrade to get quarterly collectible items!'
              : 'Join to get quarterly collectible items!'}
          </SizableText>

          <ButtonLink mx="auto" href="/perks?tier=2">
            {isLoggedIn ? 'Upgrade All Access' : 'Join All Access'}
          </ButtonLink>
        </YStack>
      )}
    </Section>
  )
}

export default QuarterlyCollectibles
